/**
 * Player profile: stable client id, display name and flag. Persisted in localStorage.
 */
import { signal } from "@preact/signals";
import { isRecord, randomId, readJSON, str, writeJSON } from "./local";

export interface Profile {
  /** Stable random identity used by the multiplayer server for rejoin. */
  clientID: string;
  name: string;
  /** ISO-3166 alpha-2 (lowercase) or a special emoji flag; null = none. */
  flag: string | null;
  createdAt: number;
}

const KEY = "profile.v1";
export const NAME_MAX_LENGTH = 24;
export const NAME_MIN_LENGTH = 2;

const NAME_PARTS_A = ["Iron", "Swift", "Silent", "Crimson", "Azure", "Golden", "Stone", "Storm", "Frost", "Ember", "Shadow", "Bright"];
const NAME_PARTS_B = ["Falcon", "Warden", "Marshal", "Tide", "Lion", "Fox", "Wolf", "Comet", "Harbor", "Ridge", "Raven", "Vanguard"];

/** A friendly random default name such as "StormFalcon42". */
export function randomPlayerName(): string {
  const r = (n: number) => Math.floor(Math.random() * n);
  return NAME_PARTS_A[r(NAME_PARTS_A.length)] + NAME_PARTS_B[r(NAME_PARTS_B.length)] + (10 + r(90));
}

/**
 * Clean a user-entered name: strip control / zero-width characters, collapse whitespace,
 * trim and clamp to NAME_MAX_LENGTH code points.
 */
export function sanitizeName(raw: string): string {
  const cleaned = raw
    // eslint-disable-next-line no-control-regex
    .replace(/[\u0000-\u001f\u007f-\u009f​-‏‪-‮⁠-⁯﻿]/g, "")
    .replace(/\s+/g, " ")
    .trim();
  return Array.from(cleaned).slice(0, NAME_MAX_LENGTH).join("").trim();
}

/** Flags are either a 2-letter ISO code or a short emoji string. */
export function sanitizeFlag(flag: unknown): string | null {
  if (typeof flag !== "string" || flag.length === 0) return null;
  if (/^[a-z]{2}$/i.test(flag)) return flag.toLowerCase();
  if (flag.length <= 16 && !/[<>"'&]/.test(flag)) return flag;
  return null;
}

function createProfile(): Profile {
  return { clientID: randomId(20), name: randomPlayerName(), flag: null, createdAt: Date.now() };
}

export function loadProfile(): Profile {
  const raw = readJSON<unknown>(KEY, null);
  if (!isRecord(raw)) {
    const p = createProfile();
    writeJSON(KEY, p);
    return p;
  }
  const clientID = str(raw.clientID, "", 64);
  const name = sanitizeName(str(raw.name, "", 64));
  const profile: Profile = {
    clientID: /^[a-z0-9]{8,64}$/i.test(clientID) ? clientID : randomId(20),
    name: name.length >= NAME_MIN_LENGTH ? name : randomPlayerName(),
    flag: sanitizeFlag(raw.flag),
    createdAt: typeof raw.createdAt === "number" ? raw.createdAt : Date.now(),
  };
  if (profile.clientID !== clientID || profile.name !== raw.name) writeJSON(KEY, profile);
  return profile;
}

export function saveProfile(p: Profile): void {
  writeJSON(KEY, p);
}

/** Reactive profile store. */
export const profile = signal<Profile>(loadProfile());

/** Update name / flag. The name is sanitized; invalid names are ignored. */
export function updateProfile(patch: { name?: string; flag?: string | null }): void {
  const cur = profile.value;
  const next: Profile = { ...cur };
  if (patch.name !== undefined) {
    const n = sanitizeName(patch.name);
    if (Array.from(n).length >= NAME_MIN_LENGTH) next.name = n;
  }
  if (patch.flag !== undefined) next.flag = sanitizeFlag(patch.flag);
  profile.value = next;
  saveProfile(next);
}

/** Re-read from storage (tests / other tabs). */
export function reloadProfile(): void {
  profile.value = loadProfile();
}
